import { useLazyQuery } from '@provision/graphql-client';
import { useCallback, useRef, useState } from 'react';
import {
  GetSubscriptionsReportQuery,
  SubscriptionsReportRow,
} from 'src/graphql/entities/subscriptions-report/subscriptions-report.types';
import { GET_SUBSCRIPTIONS_REPORT_QUERY } from 'src/graphql/entities/subscriptions-report/supscriptions-report.operations';
import { removeDuplicateRows } from 'src/pages/Reports/utils';
import { useNotifications } from 'src/utils/hooks';

const DEFAULT_LIMIT = 40;

type UseSubscriptionsReportParams = {
  partnerId: number | string;
  status: string;
  limit?: number | null;
};

export const useSubscriptionsReport = (params: UseSubscriptionsReportParams) => {
  const { showErrorAlert } = useNotifications();
  const [records, setRecords] = useState<SubscriptionsReportRow[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const isFetchingRef = useRef(false);

  const [runQuery] = useLazyQuery<GetSubscriptionsReportQuery>(
    GET_SUBSCRIPTIONS_REPORT_QUERY,
    { fetchPolicy: 'network-only' },
  );

  const loadAll = useCallback(async () => {
    if (!params.partnerId || isFetchingRef.current) return;

    setLoading(true);
    isFetchingRef.current = true;

    try {
      let nextPage: string | null | undefined = null;
      let accumulatedRecords: SubscriptionsReportRow[] = [];

      do {
        const { data } = await runQuery({
          variables: {
            params: {
              partnerId: params.partnerId,
              status: params.status,
              nextPage,
              limit: params.limit || DEFAULT_LIMIT,
            },
          },
        });

        if (!data) break;

        const report = data.getSubscriptionsReport;

        accumulatedRecords = removeDuplicateRows([...accumulatedRecords, ...report.records]);
        setRecords(accumulatedRecords);
        setTotalRecords(report.pagination?.totalCount || accumulatedRecords.length);
        nextPage = report.pagination?.nextPage;
      } while (nextPage);
    } catch (err) {
      console.error(err);
      showErrorAlert('Something went wrong');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [params.partnerId, params.status, params.limit, runQuery, showErrorAlert]);

  const reset = useCallback(() => {
    setRecords([]);
    setTotalRecords(0);
    setLoading(false);
    isFetchingRef.current = false;
  }, []);

  return {
    data: records,
    totalRecords,
    loading,
    loadAll,
    reset,
  };
};
